import type { IconType } from 'react-icons';
import {
    BsFiletypeDocx,
    BsFiletypeDoc,
    BsFiletypeXlsx,
    BsFiletypeXls,
    BsFiletypePdf,
    BsFiletypePptx,
    BsFiletypePpt,
    BsFiletypeTxt,
    BsFiletypeCsv,
    BsFiletypeXml,
    BsFiletypeJson,
    BsFiletypeJpg,
    BsFiletypePng,
    BsFiletypeSvg,
    BsFileEarmark,
    BsFileEarmarkZip,
} from 'react-icons/bs';
import { HiOutlineLink } from 'react-icons/hi2';

/** Иконка типа файла + акцентные цвета (по расширению). Общий источник для
 *  раздела «Документация» и грида вложений Front и студии документации CP — единый визуальный язык.
 *  Цвета — CSS-значения для style/кастомных свойств: где есть токен палитры, стоит
 *  var(--…) (перекрашивается вместе с темой), остальные — фирменные литералы. */
export interface IFileTypeInfo {
    Icon: IconType;
    label: string;
    accent: string;
    accentSoft: string;
    accentInk: string;
}

/** Акцент из токена палитры + мягкая подложка той же краски (альфа в процентах). */
const fromToken = (name: string, softPercent: number) => ({
    accent: `var(--${name})`,
    accentSoft: `color-mix(in srgb, var(--${name}) ${softPercent}%, transparent)`,
});

export const FILE_TYPES: Record<string, IFileTypeInfo> = {
    docx: { Icon: BsFiletypeDocx, label: 'Word', ...fromToken('primary-50', 14), accentInk: 'var(--primary-70)' },
    doc: { Icon: BsFiletypeDoc, label: 'Word', ...fromToken('primary-50', 14), accentInk: 'var(--primary-70)' },
    xlsx: { Icon: BsFiletypeXlsx, label: 'Excel', ...fromToken('success-50-main', 14), accentInk: 'var(--success-70)' },
    xls: { Icon: BsFiletypeXls, label: 'Excel', ...fromToken('success-50-main', 14), accentInk: 'var(--success-70)' },
    pdf: { Icon: BsFiletypePdf, label: 'PDF', ...fromToken('error-60-main', 14), accentInk: 'var(--error-70)' },
    pptx: {
        Icon: BsFiletypePptx,
        label: 'PowerPoint',
        ...fromToken('warning-50-main', 14),
        accentInk: 'var(--warning-70)',
    },
    ppt: {
        Icon: BsFiletypePpt,
        label: 'PowerPoint',
        ...fromToken('warning-50-main', 14),
        accentInk: 'var(--warning-70)',
    },
    txt: { Icon: BsFiletypeTxt, label: 'Text', ...fromToken('neutral-60', 14), accentInk: 'var(--neutral-80)' },
    csv: { Icon: BsFiletypeCsv, label: 'CSV', ...fromToken('success-50-main', 14), accentInk: 'var(--success-70)' },
    xml: { Icon: BsFiletypeXml, label: 'XML', ...fromToken('purple-60-main', 14), accentInk: 'var(--purple-70)' },
    json: { Icon: BsFiletypeJson, label: 'JSON', ...fromToken('purple-60-main', 14), accentInk: 'var(--purple-70)' },
    jpg: {
        Icon: BsFiletypeJpg,
        label: 'JPEG',
        accent: '#b08cff',
        accentSoft: 'rgba(176, 140, 255, 0.18)',
        accentInk: 'var(--purple-70)',
    },
    jpeg: {
        Icon: BsFiletypeJpg,
        label: 'JPEG',
        accent: '#b08cff',
        accentSoft: 'rgba(176, 140, 255, 0.18)',
        accentInk: 'var(--purple-70)',
    },
    png: {
        Icon: BsFiletypePng,
        label: 'PNG',
        accent: '#b08cff',
        accentSoft: 'rgba(176, 140, 255, 0.18)',
        accentInk: 'var(--purple-70)',
    },
    svg: {
        Icon: BsFiletypeSvg,
        label: 'SVG',
        accent: '#4cc8ff',
        accentSoft: 'rgba(76, 200, 255, 0.18)',
        accentInk: 'var(--cyan-70)',
    },
    zip: { Icon: BsFileEarmarkZip, label: 'Архив', ...fromToken('neutral-60', 16), accentInk: 'var(--neutral-80)' },
    rar: { Icon: BsFileEarmarkZip, label: 'Архив', ...fromToken('neutral-60', 16), accentInk: 'var(--neutral-80)' },
};

export const DEFAULT_FILE_TYPE: IFileTypeInfo = {
    Icon: BsFileEarmark,
    label: 'Файл',
    ...fromToken('neutral-60', 14),
    accentInk: 'var(--neutral-80)',
};

/** Инфо о типе файла по расширению (регистронезависимо). */
export const fileType = (ext: string | undefined): IFileTypeInfo =>
    (ext && FILE_TYPES[ext.toLowerCase()]) || DEFAULT_FILE_TYPE;

/** Документ-ссылка вместо файла (раздел «Документация» Front, студия CP). */
export const LINK_TYPE: IFileTypeInfo = {
    Icon: HiOutlineLink,
    label: 'Ссылка',
    ...fromToken('cyan-60-main', 14),
    accentInk: 'var(--cyan-70)',
};
